import { useAuth as useAuthContext } from '../contexts/AuthContext';

// Re-export the useAuth hook from context
export const useAuth = useAuthContext;

// Additional auth-related hooks can be added here
export const usePermissions = () => {
  const { user, permissions, features } = useAuth();
  
  const hasRole = (role: string | string[]) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };
  
  const hasPermission = (permission: string) => {
    if (!permissions) return false;
    return permissions[permission] === true;
  };
  
  const hasFeature = (feature: string) => {
    if (!features) return false;
    return features.includes(feature);
  };
  
  const isAdmin = () => hasRole('admin');
  const isAgent = () => hasRole('agent');
  const isUser = () => hasRole('user');
  const isAgentOrAdmin = () => hasRole(['agent', 'admin']);
  const isAuthenticated = () => {
    // Validate that user exists and has required fields
    if (!user) return false;
    if (!user.id || !user.username || !user.email) return false;
    // Ensure user has a valid role
    if (!user.role || !['user', 'agent', 'admin'].includes(user.role)) return false;
    return true;
  };
  
  // Check if agent is approved (for agent-specific features)
  // An agent is considered approved if:
  // 1. Their role is 'agent' AND adminApprovalStatus is 'approved', OR
  // 2. Their role is 'agent' (the backend effectiveRole already enforces approval;
  //    if the backend returned role='agent', the user passed the approval gate)
  const isApprovedAgent = () => {
    if (!isAgent()) return false;
    // If adminApprovalStatus is present, check it explicitly
    if (user?.adminApprovalStatus) {
      return user.adminApprovalStatus === 'approved';
    }
    // If adminApprovalStatus is missing but role is 'agent', trust the backend
    // (effectiveRole already downgrades unapproved agents to 'user')
    return true;
  };
  
  return {
    hasRole,
    hasPermission,
    hasFeature,
    isAdmin,
    isAgent,
    isUser,
    isAgentOrAdmin,
    isApprovedAgent,
    isAuthenticated,
    user,
    permissions,
    features,
  };
};

export default useAuth;
